import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import {
    Category,
    Producto,
    ExternalApis
} from '@app/core/services/api/API.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { sortJSON } from '../../util.service';
import { takeUntil } from 'rxjs/operators';
import { CredentialsCurrentUser, CredentialsService } from '@app/core';
import { ModificatorPremiumProductService } from '@app/shared/services/discount-products.service';
import { ClientsService } from '@app/shared/services/preloaded/auth/clients.service';
import { ExternalApisService } from '@app/shared/services/preloaded/unauth/external-apis.service';
import { BrandfactoryServerlessService } from '../../serverless/brandfactory-serverless.service';
import { SlsAdminSellerService } from '../../serverless/sls-admin-seller.service';
import { DetailProduct } from '@app/shared/interfaces';
import { ROLES_GROUPS } from '@app/constants';
import { environment } from '@env/environment';
import { UserClientOptionService } from './user-client-option.service';
import { resolvePremiumsCatalogLoadContext } from './premiums-catalog-load-context.util';

@Injectable({
    providedIn: 'root'
})
export class BestSellersService implements OnDestroy {
    private _bestSellers$ = new BehaviorSubject<Producto[]>([]);
    private _panggaProducts$ = new BehaviorSubject<DetailProduct[]>([]);
    private externalAPIs = signal<ExternalApis[]>([]);
    private _brandfactoryServerlessService = inject(
        BrandfactoryServerlessService
    );
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _modificatorPremiumProductService = inject(
        ModificatorPremiumProductService
    );
    private _clientsService = inject(ClientsService);
    private _externalApisService = inject(ExternalApisService);
    private _slsAdminSellerService = inject(SlsAdminSellerService);
    private _userClientOptionService = inject(UserClientOptionService);
    private _credentialsService = inject(CredentialsService);

    private _isLoading = false;
    private _lastLoadedUser:
        | CredentialsCurrentUser
        | null
        | undefined = undefined;

    constructor() {
        this._externalApisService.externalApis$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((externalAPIs: ExternalApis[]) => {
                this.externalAPIs.set(externalAPIs);
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(true);
        this._unsubscribeAll.complete();
    }

    get bestSellers$() {
        return this._bestSellers$.asObservable();
    }

    get panggaProducts$() {
        return this._panggaProducts$.asObservable();
    }

    getBestSellersSnapshot(): Producto[] {
        return this._bestSellers$.getValue() ?? [];
    }

    wasLoaded(): boolean {
        return this._lastLoadedUser !== undefined;
    }

    async listBestSellers(currentUser: CredentialsCurrentUser | null) {
        if (!currentUser) {
            currentUser = this._credentialsService.credentialsCurrentUser;
        }

        if (this._isLoading) {
            return;
        }

        const userKey = currentUser
            ? `${currentUser.id || currentUser.email || 'unknown'}-${
                  currentUser.role
              }`
            : 'null';
        const lastUserKey = this._lastLoadedUser
            ? `${this._lastLoadedUser.id ||
                  this._lastLoadedUser.email ||
                  'unknown'}-${this._lastLoadedUser.role}`
            : 'null';

        if (
            this._lastLoadedUser !== undefined &&
            userKey === lastUserKey &&
            this._bestSellers$.getValue().length > 0 &&
            this._panggaProducts$.getValue().length > 0
        ) {
            return;
        }

        this._isLoading = true;
        try {
            const { products, panggaProducts } = await this.listBestSellersBD(
                currentUser
            );
            this._panggaProducts$.next(panggaProducts);
            this._bestSellers$.next(products);
            this._lastLoadedUser = currentUser;
        } catch (err) {
            this._bestSellers$.next([]);
            this._panggaProducts$.next([]);
        } finally {
            this._isLoading = false;
        }
    }

    /**
     * Helper to map, filter, and apply discounts to a product list
     */
    private async processProducts(
        products: DetailProduct[],
        categories: Category[],
        currentUser: CredentialsCurrentUser
    ): Promise<DetailProduct[]> {
        const mappedProducts = products.map((product: DetailProduct) => {
            product.category = categories?.find(
                (c: Category) => c.id === product.categoryId
            );
            return product;
        });
        // .filter((p: DetailProduct) => p.category);

        if (ROLES_GROUPS.MKT_GROUP.includes(currentUser.role)) {
            const client = await this._clientsService.findClientById(
                currentUser.clientId
            );
            for (const product of mappedProducts) {
                this._modificatorPremiumProductService.calculateIncrement(
                    product,
                    client,
                    this.externalAPIs()
                );
                this._modificatorPremiumProductService.calculateDiscounts(
                    product,
                    client,
                    this.externalAPIs()
                );
            }
        }
        return mappedProducts;
    }

    private async listBestSellersBD(
        currentUser: CredentialsCurrentUser | null
    ): Promise<{
        products: DetailProduct[];
        panggaProducts: DetailProduct[];
    }> {
        try {
            const useAuth = currentUser !== null;
            const loadContext = await resolvePremiumsCatalogLoadContext(
                currentUser,
                this._userClientOptionService
            );
            const bestSellerOptions = useAuth
                ? {
                      ...(loadContext.clientId
                          ? { clientId: loadContext.clientId }
                          : {}),
                      useAuth: true
                  }
                : {
                      showPublicPrices: environment.showPublicPrices,
                      useAuth: false
                  };
            const [panggaResponse, response] = await Promise.all([
                this._slsAdminSellerService.listPanggaProducts(useAuth),
                this._brandfactoryServerlessService.bestSellerProducts(
                    bestSellerOptions
                )
            ]);

            if (!response) {
                return { products: [], panggaProducts: [] };
            }

            let products: DetailProduct[] = [];
            if (response.products && Array.isArray(response.products)) {
                if (currentUser) {
                    products = await this.processProducts(
                        response.products,
                        response.categories || [],
                        currentUser
                    );
                } else {
                    products = response.products.map(
                        (product: DetailProduct) => {
                            product.category = (response.categories || []).find(
                                (c: Category) => c.id === product.categoryId
                            );
                            return product;
                        }
                    );
                }
            }

            let panggaProducts: DetailProduct[] = [];
            if (panggaResponse) {
                if (currentUser) {
                    panggaProducts = await this.processProducts(
                        panggaResponse.products || [],
                        panggaResponse.categories || [],
                        currentUser
                    );
                } else {
                    panggaProducts = (panggaResponse.products || []).map(
                        (product: DetailProduct) => {
                            product.category = (
                                panggaResponse.categories || []
                            ).find(
                                (c: Category) => c.id === product.categoryId
                            );
                            return product;
                        }
                    );
                }
            }

            return {
                products,
                panggaProducts
            };
        } catch (err) {
            return { products: [], panggaProducts: [] };
        }
    }
}
